import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  IconButton,
  Box,
} from '@mui/material';
import { 
  Close as CloseIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface ForeignKeyError {
  message: string;
  errorDetails?: {
    userInfo?: {
      id: number;
      name: string;
      email: string;
    };
    relatedData?: Array<{
      table: string;
      count: number;
      examples?: string[];
    }>;
  };
}

export interface ForeignKeyErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onForceDelete: () => void;
  error: ForeignKeyError | null;
  isDeleting: boolean;
}

const ForeignKeyErrorModal: React.FC<ForeignKeyErrorModalProps> = ({
  isOpen,
  onClose,
  onForceDelete,
  error,
  isDeleting,
}) => {
  const { t } = useTranslation();

  if (!error) return null;

  const getTableName = (table: string): string => {
    switch (table) {
      case 'Score':
        return t('foreignKeyErrorScores');
      case 'Grade':
        return t('foreignKeyErrorGrades');
      case 'Grade (as teacher)':
        return t('foreignKeyErrorGradesAsTeacher');
      case 'scores':
        return t('foreignKeyErrorScores');
      case 'reading_sessions':
        return t('foreignKeyErrorReadingSessions');
      case 'user_grades':
        return t('foreignKeyErrorUserGrades');
      default:
        return table;
    }
  };

  const getTableIcon = (table: string): string => {
    switch (table) {
      case 'Score':
        return '📊';
      case 'Grade':
        return '🏫';
      case 'Grade (as teacher)':
        return '👨‍🏫';
      case 'scores':
        return '📊';
      case 'reading_sessions':
        return '📖';
      case 'user_grades':
        return '🏫';
      default:
        return '📁';
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isDeleting ? undefined : onClose}
      className="foreign-key-error-modal message-modal"
    >
      <IconButton
        onClick={onClose}
        disabled={isDeleting}
        className="close-button"
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle component="h3">
        <ErrorIcon 
          className="error-icon" 
          sx={{ 
            backgroundColor: "#b689e3", 
            fontSize: 64, 
            // border: "6px solid #b689e3", 
            borderRadius: "50%", 
            path: { color: "white" } 
          }}
        />
        {t('foreignKeyErrorTitle')}
      </DialogTitle>

      <DialogContent>
        <p style={{ textAlign: 'start' }}>
          {t('cannotDeleteUser', { 
            userName: `"${error.errorDetails?.userInfo?.name}"`, 
            userEmail: `${error.errorDetails?.userInfo?.email}` 
          })}
        </p>

        <Box className="info-box">
          <span role="img" aria-label="רעיון">💡</span>
          <span>
            {t('whatDoesThisMean')} {t('foreignKeyErrorUserHasData')}
          </span>
        </Box>

        <Box className="warning-section">
          <Box className="warning-header">
            <span role="img" aria-label="אזהרה">⚠️</span>
            <span>{t('foreignKeyErrorWarningTitle')} - {t('foreignKeyErrorWarningMessage')}</span>
          </Box>
          
          {error.errorDetails?.relatedData && (
            <Box className="related-data">
              <Box className="data-summary">
                <strong>{t('foreignKeyErrorDataSummary')}</strong>
              </Box>
              
              <Box className="data-list">
                {error.errorDetails.relatedData.map((data, index) => (
                  <Box key={index} className="data-item">
                    <Box className="data-header">
                      <Box className="data-icon">
                        <span role="img" aria-label={getTableName(data.table)}>{getTableIcon(data.table)}</span>
                      </Box>
                      <Box className="data-name">
                        {getTableName(data.table)}
                      </Box>
                    </Box>
                    <Box className="data-count">
                      {data.count} {t('foreignKeyErrorRecords')}
                    </Box>
                    
                    {data.examples && data.examples.length > 0 && (
                      <Box className="data-examples">
                        <Box className="examples-label">{t('foreignKeyErrorExamples')}</Box>
                        <Box className="examples-list">
                          {data.examples.map((example, i) => (
                            <Box key={i} className="example-item">
                              {example}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box className="final-warning">
            <strong><span role="img" aria-label="איסור">⛔</span> {t('foreignKeyErrorFinalWarning')}</strong>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions style={{ marginBottom: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isDeleting}
          className="cancel-button"
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={onForceDelete}
          variant="contained"
          color="error"
          disabled={isDeleting}
          className="delete-button"
        >
          {isDeleting ? t('deleting') : t('deleteAnyway')}
        </Button>
      </DialogActions>

      {/* Loading Overlay */}
      {isDeleting && (
        <Box className="loading-overlay">
          <CircularProgress size={60} />
          <h4>{t('foreignKeyErrorDeletingUserAndData')}</h4>
          <p>{t('foreignKeyErrorPleaseWaitMessage')}</p>
        </Box>
      )}
    </Dialog>
  );
};

export default ForeignKeyErrorModal;